import { useEffect, useMemo, useState } from "react";
import { paymentApi } from "../../services/paymentApi";

const paymentMethods = [
  { value: "bank_transfer", label: "Bank Transfer" },
  { value: "cash", label: "Cash" },
  { value: "card", label: "Card" },
  { value: "online", label: "Online" },
  { value: "other", label: "Other" },
];

const paymentStatuses = [
  { value: "pending", label: "Pending" },
  { value: "completed", label: "Completed" },
  { value: "failed", label: "Failed" },
  { value: "refunded", label: "Refunded" },
];

const money = (value) => Number(Number(value || 0).toFixed(2));

export default function PaymentFormModal({
  open,
  onClose,
  onSaved,
  payment,
  invoices = [],
}) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const [invoiceId, setInvoiceId] = useState("");
  const [amount, setAmount] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("bank_transfer");
  const [status, setStatus] = useState("completed");
  const [paymentDate, setPaymentDate] = useState("");
  const [notes, setNotes] = useState("");

  useEffect(() => {
    if (!open) return;

    if (payment) {
      setInvoiceId(payment.invoiceId || "");
      setAmount(payment.amount !== undefined ? String(payment.amount) : "");
      setPaymentMethod(payment.paymentMethod || "bank_transfer");
      setStatus(payment.status || "completed");
      setPaymentDate(payment.paymentDate || new Date().toISOString().slice(0, 10));
      setNotes(payment.notes || "");
    } else {
      setInvoiceId("");
      setAmount("");
      setPaymentMethod("bank_transfer");
      setStatus("completed");
      setPaymentDate(new Date().toISOString().slice(0, 10));
      setNotes("");
    }

    setError("");
  }, [open, payment]);

  const selectedInvoice = useMemo(
    () => invoices.find((inv) => String(inv.id) === String(invoiceId)),
    [invoices, invoiceId]
  );

  const invoiceBalance = money(selectedInvoice?.balanceAmount);
  const invoiceTotal = money(selectedInvoice?.totalAmount);
  const invoicePaid = money(selectedInvoice?.paidAmount);

  const handleInvoiceChange = (e) => {
    const id = e.target.value;
    setInvoiceId(id);

    const inv = invoices.find((x) => String(x.id) === String(id));
    if (inv) {
      setAmount(String(money(inv.balanceAmount)));
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");

    try {
      if (!invoiceId) throw new Error("Invoice is required");
      if (!amount || Number(amount) <= 0)
        throw new Error("Amount must be greater than zero");

      const payload = {
        invoiceId: Number(invoiceId),
        amount: Number(amount),
        paymentMethod,
        status,
        paymentDate,
        notes,
      };

      const res = payment
        ? await paymentApi.update(payment.id, payload)
        : await paymentApi.create(payload);

      onSaved?.(res.data?.message || "Payment saved successfully");
      onClose();
    } catch (err) {
      setError(err.response?.data?.message || err.message || "Failed to save payment");
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  const inputClass =
    "w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100 dark:placeholder:text-slate-500";
  const labelClass = "mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-300";

  return (
    <div className="fixed inset-0 z-[9998] flex items-center justify-center bg-black/40 px-4 py-6 backdrop-blur-[2px]">
      <div className="max-h-[92vh] w-full max-w-4xl overflow-y-auto rounded-3xl border border-slate-200 bg-white shadow-2xl dark:border-slate-800 dark:bg-slate-900">
        <div className="border-b border-slate-100 px-6 py-5 dark:border-slate-800">
          <h2 className="text-2xl font-semibold text-slate-900 dark:text-slate-100">
            {payment ? "Edit Payment" : "New Payment"}
          </h2>
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
            Payments are linked to invoices and update the invoice balance when completed.
          </p>
        </div>

        <form onSubmit={submit} className="space-y-6 p-6">
          {error ? (
            <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-900 dark:bg-red-950/50 dark:text-red-300">
              {error}
            </div>
          ) : null}

          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <label className={labelClass}>Invoice *</label>
              <select
                value={invoiceId}
                onChange={handleInvoiceChange}
                className={inputClass}
                disabled={!!payment}
              >
                <option value="">Select invoice</option>
                {invoices.map((inv) => (
                  <option key={inv.id} value={inv.id}>
                    {inv.invoiceNumber} —{" "}
                    {inv.supplierName || inv.vendor?.name || "No vendor"}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className={labelClass}>Amount *</label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className={inputClass}
                placeholder={String(invoiceBalance || 0)}
              />
            </div>

            <div>
              <label className={labelClass}>Payment Method</label>
              <select
                value={paymentMethod}
                onChange={(e) => setPaymentMethod(e.target.value)}
                className={inputClass}
              >
                {paymentMethods.map((method) => (
                  <option key={method.value} value={method.value}>
                    {method.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className={labelClass}>Status</label>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                className={inputClass}
              >
                {paymentStatuses.map((s) => (
                  <option key={s.value} value={s.value}>
                    {s.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className={labelClass}>Payment Date</label>
              <input
                type="date"
                value={paymentDate}
                onChange={(e) => setPaymentDate(e.target.value)}
                className={inputClass}
              />
            </div>

            <div>
              <label className={labelClass}>Remaining Balance</label>
              <input
                readOnly
                value={`${money(invoiceBalance).toFixed(2)}`}
                className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600 outline-none dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300"
              />
            </div>

            <div className="md:col-span-2">
              <label className={labelClass}>Notes</label>
              <textarea
                rows="3"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className={inputClass}
                placeholder="Optional notes"
              />
            </div>

            <div className="md:col-span-2 rounded-2xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-950">
              <div className="grid gap-3 md:grid-cols-3">
                <div>
                  <p className="text-xs text-slate-500 dark:text-slate-400">Invoice Total</p>
                  <p className="text-base font-semibold text-slate-800 dark:text-slate-100">
                    £{invoiceTotal.toFixed(2)}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 dark:text-slate-400">Paid So Far</p>
                  <p className="text-base font-semibold text-slate-800 dark:text-slate-100">
                    £{invoicePaid.toFixed(2)}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 dark:text-slate-400">Balance</p>
                  <p className="text-base font-semibold text-slate-800 dark:text-slate-100">
                    £{invoiceBalance.toFixed(2)}
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-end gap-3 border-t border-slate-100 pt-4 dark:border-slate-800">
            <button
              type="button"
              onClick={onClose}
              className="rounded-2xl border border-slate-200 bg-white px-5 py-3 text-sm font-medium text-slate-600 transition hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-300 dark:hover:bg-slate-800"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="rounded-2xl bg-blue-600 px-6 py-3 text-sm font-medium text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-70"
            >
              {saving ? "Saving..." : payment ? "Update Payment" : "Create Payment"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}